import * as R from 'ramda';
import {
  reducerWrapper,
  serializeWithSort,
  getCustomEvents,
  getAllEvents,
  getAllEventAliases,
  getAllTopEvents,
  getCustomerEventAliases,
  hasCustomerEventAliases,
} from '../support/utils';

import { FILTER_DIMENSIONS } from '../support/eo_mapping';

import {
  GET_DRUID_DATA,
  GET_DRUID_FILTERS,
  GET_CUSTOM_DRUID_EVENTS,
  GET_ALL_DRUID_EVENTS,
  GET_ALL_TOP_DRUID_EVENTS_BY_NAME,
  GET_TOP_DRUID_CUSTOMER_EVENT_ALIASES_BY_NAME,
  GET_ALL_TOP_DRUID_CUSTOMER_EVENT_ALIASES,
  RESET_LAST_RATE_LIMIT_HIT,
  FETCH_REPORT_TOTALS,
  GET_DRUID_LIMIT,
} from '../actions/DruidActions';
import { isBefore } from '../../main/support/dateUtils';
import moment from 'moment';

export const SYSTEM_EVENTS = ['install', 'open', 'click', 'referred session', 'pageview', 'web session start'];

const DEFAULT_FILTERS = Object.keys(FILTER_DIMENSIONS).reduce((acc, curr) => {
  acc[curr] = [];
  return acc;
}, {});

const INIT_STATE = {
  /**
   * {
   *   [<SEREALIZED QUERY>]: {
   *     loading: false,
   *     error: null,
   *     data: <DRUID SERVICE RESULT>
   *   },
   *   ...
   * }
   */
  queries: {},
  reportTotals: {},
  filters: DEFAULT_FILTERS,
  custom_event_list: [],
  allEventsLast30d: [],
  allEventAliasesLast30d: {},
  hasCustomerEventAliasesLast30d: null,
  lastRateLimitHit: undefined,
  druidLimit: undefined,
};

export function createInitialState() {
  return { ...INIT_STATE };
}

export function createInitialQueryState() {
  return {
    loading: false,
    error: null,
    data: undefined,
  };
}

export function getHash(options) {
  const { query = {}, sourceQuery = {}, type, overrides, isReportQuery = false, isTrendsQuery = false } = options;
  const queryHashProps = { type, overrides, ...query, ...sourceQuery };
  if (isReportQuery) {
    queryHashProps.isReportQuery = true;
  }
  if (isTrendsQuery) {
    queryHashProps.isTrendsQuery = true;
  }
  return serializeWithSort(queryHashProps);
}

function generateLastLimitHitOrUndefined(currentLastLimit, latestLastLimit) {
  if (!currentLastLimit) {
    return latestLastLimit;
  }

  if (isBefore(moment(currentLastLimit).add(1, 'minute'), latestLastLimit)) {
    return latestLastLimit;
  }

  return currentLastLimit;
}

function clearLastLimitOrUseCurrentOne(lastRateLimitHit) {
  if (!lastRateLimitHit) {
    return undefined;
  }

  if (!isBefore(moment(lastRateLimitHit).add(1, 'minute'), Date.now())) {
    return lastRateLimitHit;
  }

  return undefined;
}

function Handler(state, action) {
  switch (action.type) {
    case GET_DRUID_DATA.request: {
      // if we don't have an data, we can't process the hash
      if (!action.data) {
        return state;
      }

      const hash = getHash(action.data);

      return R.merge(state, {
        queries: R.assoc(hash, { ...createInitialQueryState(), loading: true }, state.queries),
        lastRateLimitHit: clearLastLimitOrUseCurrentOne(state.lastRateLimitHit),
      });
    }
    case GET_DRUID_DATA.response: {
      const hash = getHash(action.data);

      return R.merge(state, {
        queries: R.assoc(
          hash,
          {
            loading: false,
            error: null,
            data: action.data.results,
            limitReached: action.data.limitReached,
            limit: action.data.limit,
            isReportData: action.data.isReportQuery,
            isTrendsData: action.data.isTrendsQuery,
            limitedResults: action.data.limitedResults,
            movedPlatformValues: action.data.movedPlatformValues,
          },
          state.queries
        ),
        lastRateLimitHit: clearLastLimitOrUseCurrentOne(state.lastRateLimitHit),
      });
    }
    case GET_DRUID_DATA.error: {
      // if we don't have an data, we can't process the hash
      if (!action.data) {
        return state;
      }

      const hash = getHash(action.data);

      return R.merge(state, {
        queries: R.assoc(
          hash,
          {
            loading: false,
            error: action.error,
            data: undefined,
          },
          state.queries
        ),
        lastRateLimitHit: generateLastLimitHitOrUndefined(state.lastRateLimitHit, action.lastRateLimitHit),
      });
    }

    case GET_DRUID_FILTERS.response:
      return R.assoc('filters', Object.assign({}, state.filters, action.data), state);
    case GET_CUSTOM_DRUID_EVENTS.response:
      return R.assoc('custom_event_list', getCustomEvents(action.data), state);

    case GET_ALL_DRUID_EVENTS.response:
      return {
        ...state,
        allEventsLast30d: getAllEvents(action.data),
        allEventAliasesLast30d: getAllEventAliases(action.data),
      };

    case GET_ALL_TOP_DRUID_EVENTS_BY_NAME.response:
      return {
        ...state,
        allEventsLast30d: getAllTopEvents(action.data),
      };

    case GET_TOP_DRUID_CUSTOMER_EVENT_ALIASES_BY_NAME.response:
      const customerEventAliases = getCustomerEventAliases(action.data.data[action.data.datasource]);
      return {
        ...state,
        allEventAliasesLast30d: {
          ...state.allEventAliasesLast30d,
          [action.data.eventName]: customerEventAliases,
        },
      };
    case GET_ALL_TOP_DRUID_CUSTOMER_EVENT_ALIASES.response:
      return {
        ...state,
        hasCustomerEventAliasesLast30d: hasCustomerEventAliases(action.data),
      };
    case RESET_LAST_RATE_LIMIT_HIT:
      return {
        ...state,
        lastRateLimitHit: undefined,
      };
    case FETCH_REPORT_TOTALS.response: {
      const hash = getHash(action.data);

      return R.merge(state, {
        reportTotals: R.assoc(
          hash,
          {
            totals: action.data.totals,
            error: action.data.error,
          },
          state.reportTotals
        ),
      });
    }
    case GET_DRUID_LIMIT.response: {
      return {
        ...state,
        druidLimit: action.data,
      };
    }
    default:
      return state;
  }
}

const DruidReducer = reducerWrapper(INIT_STATE, Handler, {
  request: [GET_DRUID_FILTERS, GET_CUSTOM_DRUID_EVENTS, GET_ALL_DRUID_EVENTS],
  error: [GET_DRUID_FILTERS, GET_CUSTOM_DRUID_EVENTS, GET_ALL_DRUID_EVENTS],
  resetOn: ['APP_CHANGE'],
});

export default DruidReducer;
